import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import OrderedList from '@tiptap/extension-ordered-list';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import classNames from 'classnames';

import { ValidationError } from '../../components';
import css from './FieldRichTextInput.module.css';

// Custom OrderedList that supports HTML type attribute (1, i, a)
const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      type: {
        default: '1',
        parseHTML: element => element.getAttribute('type') || '1',
        renderHTML: attributes => {
          return { type: attributes.type };
        },
      },
    };
  },
});

const CONTENT_MAX_LENGTH = 5000;

// Strip HTML tags to get plain text and its length
const getPlainTextLength = html => {
  if (!html) return 0;
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '').length;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').length;
};

// Convert HTML to plain text
const htmlToPlainText = html => {
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '');
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// ============================================================
// Custom Modal Component
// ============================================================
const EditorModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={css.modalContent} onClick={e => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <h3 className={css.modalTitle}>{title}</h3>
          <button type="button" className={css.modalCloseButton} onClick={onClose}>
            &times;
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ============================================================
// Link Modal
// ============================================================
const LinkModal = ({ isOpen, onClose, editor }) => {
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const previousUrl = editor?.getAttributes('link').href || '';
      setUrl(previousUrl || 'https://');

      // Get selected text
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, '');
      setText(selectedText);

      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, editor]);

  const handleInsert = () => {
    if (!url || url === 'https://') {
      // Remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (hasSelection) {
        if (text && text !== editor.state.doc.textBetween(from, to, '')) {
          editor
            .chain()
            .focus()
            .deleteSelection()
            .insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`)
            .run();
        } else {
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
        }
      } else if (text) {
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`)
          .run();
      } else {
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`)
          .run();
      }
    }
    onClose();
  };

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsert();
    }
  };

  return (
    <EditorModal isOpen={isOpen} onClose={onClose} title="Insert Link">
      <div className={css.modalForm}>
        <div className={css.modalField}>
          <label className={css.modalLabel}>Display Text</label>
          <input
            type="text"
            className={css.modalInput}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Link text (optional)"
            ref={inputRef}
          />
        </div>
        <div className={css.modalField}>
          <label className={css.modalLabel}>URL</label>
          <input
            type="text"
            className={css.modalInput}
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com"
          />
        </div>
        <div className={css.modalActions}>
          {editor?.isActive('link') && (
            <button type="button" className={css.modalButtonRemove} onClick={handleRemoveLink}>
              Remove Link
            </button>
          )}
          <div className={css.modalActionsRight}>
            <button type="button" className={css.modalButtonCancel} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className={css.modalButtonSubmit} onClick={handleInsert}>
              Insert Link
            </button>
          </div>
        </div>
      </div>
    </EditorModal>
  );
};

// ============================================================
// Image Modal
// ============================================================
const ImageModal = ({ isOpen, onClose, editor }) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleInsert = () => {
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
    onClose();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsert();
    }
  };

  return (
    <EditorModal isOpen={isOpen} onClose={onClose} title="Insert Image">
      <div className={css.modalForm}>
        <div className={css.modalField}>
          <label className={css.modalLabel}>Image URL</label>
          <input
            type="text"
            className={css.modalInput}
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com/image.jpg"
            ref={inputRef}
          />
        </div>
        <div className={css.modalActions}>
          <div className={css.modalActionsRight}>
            <button type="button" className={css.modalButtonCancel} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className={css.modalButtonSubmit} onClick={handleInsert}>
              Insert Image
            </button>
          </div>
        </div>
      </div>
    </EditorModal>
  );
};

// ============================================================
// YouTube Modal
// ============================================================
const YoutubeModal = ({ isOpen, onClose, editor }) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleInsert = () => {
    if (url) {
      editor.commands.setYoutubeVideo({ src: url, width: 480, height: 270 });
    }
    onClose();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsert();
    }
  };

  return (
    <EditorModal isOpen={isOpen} onClose={onClose} title="Embed YouTube Video">
      <div className={css.modalForm}>
        <div className={css.modalField}>
          <label className={css.modalLabel}>YouTube URL</label>
          <input
            type="text"
            className={css.modalInput}
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://www.youtube.com/watch?v=..."
            ref={inputRef}
          />
          <span className={css.modalHint}>
            Paste a YouTube video URL, short URL, or embed URL
          </span>
        </div>
        <div className={css.modalActions}>
          <div className={css.modalActionsRight}>
            <button type="button" className={css.modalButtonCancel} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className={css.modalButtonSubmit} onClick={handleInsert}>
              Embed Video
            </button>
          </div>
        </div>
      </div>
    </EditorModal>
  );
};

// ============================================================
// Toolbar button — uses onMouseDown to prevent focus steal
// ============================================================
const ToolbarButton = ({ onClick, isActive, title, children, disabled }) => (
  <button
    type="button"
    className={classNames(css.toolbarButton, {
      [css.toolbarButtonActive]: isActive,
      [css.toolbarButtonDisabled]: disabled,
    })}
    onMouseDown={e => {
      e.preventDefault(); // Prevent editor from losing focus/selection
      if (!disabled && onClick) {
        onClick();
      }
    }}
    title={title}
    disabled={disabled}
  >
    {children}
  </button>
);

// Toolbar separator
const ToolbarSeparator = () => <div className={css.toolbarSeparator} />;

// ============================================================
// Toolbar — subscribes to editor transactions for live active state
// ============================================================
const Toolbar = ({ editor, onOpenLinkModal, onOpenImageModal, onOpenYoutubeModal }) => {
  // Force re-render on every editor transaction so active states update immediately
  const [, setUpdateCounter] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => setUpdateCounter(c => c + 1);
    editor.on('transaction', handler);
    return () => {
      editor.off('transaction', handler);
    };
  }, [editor]);

  if (!editor) return null;

  return (
    <div className={css.toolbar}>
      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          &#8617;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          &#8618;
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <span style={{ textDecoration: 'underline' }}>U</span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          H3
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          &#8226; List
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            if (editor.isActive('orderedList', { type: '1' })) {
              editor.chain().focus().toggleOrderedList().run();
            } else {
              editor.chain().focus().toggleOrderedList().updateAttributes('orderedList', { type: '1' }).run();
            }
          }}
          isActive={editor.isActive('orderedList', { type: '1' })}
          title="Numbered List (1, 2, 3)"
        >
          1. List
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            if (editor.isActive('orderedList', { type: 'i' })) {
              editor.chain().focus().toggleOrderedList().run();
            } else {
              editor.chain().focus().toggleOrderedList().updateAttributes('orderedList', { type: 'i' }).run();
            }
          }}
          isActive={editor.isActive('orderedList', { type: 'i' })}
          title="Roman List (i, ii, iii)"
        >
          i. List
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
          disabled={!editor.can().sinkListItem('listItem')}
          title="Increase Indent"
        >
          &#8594;|
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().liftListItem('listItem').run()}
          disabled={!editor.can().liftListItem('listItem')}
          title="Decrease Indent"
        >
          |&#8592;
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          &#8676;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          &#8596;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          &#8677;
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      {/* <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={onOpenLinkModal}
          isActive={editor.isActive('link')}
          title="Insert Link"
        >
          Link
        </ToolbarButton>
        <ToolbarButton onClick={onOpenImageModal} title="Insert Image">
          Image
        </ToolbarButton>
        <ToolbarButton onClick={onOpenYoutubeModal} title="Embed YouTube Video">
          Video
        </ToolbarButton>
      </div> */}

      <ToolbarSeparator />

      <div className={css.toolbarGroup}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Blockquote"
        >
          &ldquo;&rdquo;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          &mdash;
        </ToolbarButton>
      </div>
    </div>
  );
};

// ============================================================
// Main Editor Component
// ============================================================
const RichTextEditorComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    customErrorText,
    placeholder,
    maxLength,
    hideErrorMessage,
    ...rest
  } = props;

  const { valid, invalid, touched, error } = meta;
  const errorText = customErrorText || error;
  const hasError = !!customErrorText || !!(touched && invalid && error);
  const fieldMeta = { touched: hasError, error: errorText };

  const charLimit = maxLength || CONTENT_MAX_LENGTH;
  const isInitialMount = useRef(true);

  // Modal states
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        orderedList: false,
        link: false,
        underline: false,
      }),
      CustomOrderedList,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Image.configure({ inline: false }),
      Youtube.configure({ inline: false, ccLanguage: 'en' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: input.value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const plainTextLen = getPlainTextLength(html);

      if (plainTextLen <= charLimit) {
        input.onChange(html);
      }
    },
    onBlur: () => {
      input.onBlur();
    },
  });

  // Sync editor content if the form value changes externally (e.g. initialValues)
  useEffect(() => {
    if (editor && isInitialMount.current) {
      isInitialMount.current = false;
      if (input.value && input.value !== editor.getHTML()) {
        editor.commands.setContent(input.value, false);
      }
    }
  }, [editor, input.value]);

  const currentLength = editor ? getPlainTextLength(editor.getHTML()) : 0;

  const classes = classNames(rootClassName || css.root, className);
  const editorWrapperClasses = classNames(css.editorWrapper, {
    [css.editorWrapperError]: hasError,
    [css.editorWrapperFocused]: editor?.isFocused,
  });

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      <div className={editorWrapperClasses}>
        <Toolbar
          editor={editor}
          onOpenLinkModal={() => setLinkModalOpen(true)}
          onOpenImageModal={() => setImageModalOpen(true)}
          onOpenYoutubeModal={() => setYoutubeModalOpen(true)}
        />
        <EditorContent editor={editor} className={css.editorContent} id={id} />
      </div>
      <div className={css.footer}>
        <span className={css.charCount}>
          {currentLength}/{charLimit}
        </span>
      </div>
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}

      {/* Modals */}
      <LinkModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        editor={editor}
      />
      {/* <ImageModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        editor={editor}
      />
      <YoutubeModal
        isOpen={youtubeModalOpen}
        onClose={() => setYoutubeModalOpen(false)}
        editor={editor}
      /> */}
    </div>
  );
};

// Final Form Field wrapper
const FieldRichTextInput = props => {
  const { name, ...rest } = props;
  return <Field name={name} component={RichTextEditorComponent} {...rest} />;
};

export { htmlToPlainText };
export default FieldRichTextInput;
